import { ApiResponse, GlobalStore, GridStore } from '@roc/feature-app-core';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import { GalleryService } from '../services/galleryService';
import { GalleryPhotosStore } from './galleryPhotosStore';
import {
  GalleryFolder,
  GalleryUser,
  LoanInspectionSummary,
} from '../types/galleryTypes';

export class GalleryStore {
  private globalStore: GlobalStore;
  galleryService: GalleryService;
  galleryPhotosStore: GalleryPhotosStore;
  usersGridStore: GridStore;

  selectedUser?: GalleryUser;
  folders: GalleryFolder[] = [];
  selectedFolderId?: string;
  isLoading: boolean = false;

  movePhotosOpen = false;
  /** Destination inspections found for the loan id typed in the move photos dialog. */
  loanInspections: LoanInspectionSummary[] = [];
  /** True once a search has run, so the dialog can tell "not searched yet" from "no results". */
  inspectionsSearched = false;
  isSearchingInspections = false;
  isCopyingPhotos = false;

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.galleryService = new GalleryService();
    this.galleryPhotosStore = new GalleryPhotosStore(
      this.globalStore,
      this.galleryService
    );
    this.usersGridStore = new GridStore(() => this.fetchUsers(), undefined, 15);

    makeObservable(this, {
      selectedUser: observable,
      folders: observable,
      selectedFolderId: observable,
      isLoading: observable,
      openUser: flow,
      selectFolder: flow,
      closeUser: action,
      reset: action,
      selectedFolder: computed,
      movePhotosOpen: observable,
      openMovePhotosDialog: action,
      closeMovePhotosDialog: action,
      loanInspections: observable,
      inspectionsSearched: observable,
      isSearchingInspections: observable,
      isCopyingPhotos: observable,
      searchInspectionsByLoanId: flow,
      copyPhotosToInspection: flow,
      resetMovePhotosData: action,
    });

    this.reset();
  }

  private fetchUsers(): Promise<ApiResponse> {
    const {
      dropdownFilters,
      filters,
      pageNumber,
      pageSize,
      sortBy,
      sortDir,
    } = this.usersGridStore.gridData.meta;

    return this.galleryService.getUsers(
      pageNumber,
      pageSize,
      sortBy,
      sortDir,
      filters,
      dropdownFilters
    );
  }

  *openUser(user: GalleryUser) {
    this.selectedUser = user;
    this.folders = [];
    this.selectedFolderId = undefined;
    this.galleryPhotosStore.reset();
    this.isLoading = true;
    try {
      const response: ApiResponse = yield this.galleryService.getFolders(user.userId);
      if (response?.data?.status === 'OK') {
        this.folders = response?.data?.data?.folders || [];
        if (this.folders.length > 0) {
          yield this.selectFolder(this.folders[0].folderId);
        }
      } else {
        throw new Error('There was an error while retrieving the user folders.');
      }
    } catch (err) {
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error while retrieving the user folders.',
      });
    } finally {
      this.isLoading = false;
    }
  }

  *selectFolder(folderId: string) {
    this.selectedFolderId = folderId;
    if (this.selectedUser && folderId) {
      yield this.galleryPhotosStore.loadPhotos(this.selectedUser.userId, folderId);
    }
  }

  closeUser() {
    this.selectedUser = undefined;
    this.folders = [];
    this.selectedFolderId = undefined;
    this.galleryPhotosStore.reset();
    this.resetMovePhotosData();
  }

  /**
   * Loads the DRAWS / INTERNAL_VALUATION inspections of a loan, most recently created first,
   * so the user can pick one as the destination of a photo copy.
   */
  *searchInspectionsByLoanId(loanId: string) {
    const trimmedLoanId = loanId?.trim();
    if (!trimmedLoanId) {
      return;
    }

    this.isSearchingInspections = true;
    this.loanInspections = [];
    try {
      const response: ApiResponse = yield this.galleryService.getInspectionsByLoanId(
        trimmedLoanId
      );
      if (response?.data?.status === 'OK') {
        // Already ordered newest first by the backend.
        this.loanInspections = response?.data?.data || [];
      } else {
        throw new Error('There was an error while retrieving the inspections.');
      }
    } catch (err) {
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message:
          (err as any)?.response?.data?.error?.message ||
          'There was an error while retrieving the inspections for this loan.',
      });
    } finally {
      this.inspectionsSearched = true;
      this.isSearchingInspections = false;
    }
  }

  /**
   * Copies the given gallery photos into an inspection, keeping the gallery originals, and
   * dismisses the dialog once they are through. A failure leaves it open so the user can retry.
   */
  *copyPhotosToInspection(photoUuids: string[], inspectionId: number) {
    if (!this.selectedUser || !photoUuids?.length || !inspectionId) {
      return;
    }

    this.isCopyingPhotos = true;
    try {
      const response: ApiResponse = yield this.galleryService.copyPhotosToInspection(
        this.selectedUser.userId,
        photoUuids,
        inspectionId
      );
      if (response?.data?.status !== 'OK') {
        throw new Error('There was an error while copying the photos.');
      }
      const photosCopied = response?.data?.data?.photosCopied ?? photoUuids.length;
      this.globalStore.notificationStore.showSuccessNotification({
        message: `${photosCopied} photo${
          photosCopied === 1 ? '' : 's'
        } copied to the inspection report successfully.`,
      });
      this.closeMovePhotosDialog();
    } catch (err) {
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message:
          (err as any)?.response?.data?.error?.message ||
          'There was an error while copying the photos to the inspection report.',
      });
    } finally {
      this.isCopyingPhotos = false;
    }
  }

  openMovePhotosDialog() {
    this.resetMovePhotosData();
    this.movePhotosOpen = true;
  }

  closeMovePhotosDialog() {
    this.movePhotosOpen = false;
  }

  resetMovePhotosData() {
    this.movePhotosOpen = false;
    this.loanInspections = [];
    this.inspectionsSearched = false;
    this.isSearchingInspections = false;
    this.isCopyingPhotos = false;
  }

  get selectedFolder(): GalleryFolder | undefined {
    return this.folders?.find(folder => folder.folderId === this.selectedFolderId);
  }

  reset() {
    this.selectedUser = undefined;
    this.folders = [];
    this.selectedFolderId = undefined;
    this.isLoading = false;
    this.galleryPhotosStore.reset();
    this.resetMovePhotosData();
  }
}
